import { buildUvInsertCartLineItem } from '@shared/view/uv-inserts/build-cart-line-item';
import { VISUALISER_ROUTE_META } from '../uv-inserts/route-meta';
import { findThemeIndex, seedTextByKey } from '../uv-inserts/config-state';

/**
 * Step 3's Merry Christmas card, whose message is written on the card (Figma frame 6): the option that
 * preselects a design, its card config, that design (the first one when none matches) and the card's message
 * field. Null when the option is absent (flag off), its config is inactive or the card has no message field;
 * then CONTINUE opens the gold-foil picker as before.
 * @param {{ options: Array<Record<string, any>>, resolved: import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts | null }} args
 */
export function findInlineCard({ options = [], resolved }) {
  const optionIndex = options.findIndex((option) => option.preselectTheme);
  const option = options[optionIndex];
  const meta = option ? VISUALISER_ROUTE_META[option.clicksTo] : undefined;
  const config = meta && resolved?.configs.find((candidate) => candidate.handle === meta.configHandle);
  if (!config?.active) return null;

  // Figma always shows the field; until a Christmas design exists the card is the first design.
  const themeIndex = Math.max(findThemeIndex(config.themes ?? [], option.preselectTheme), 0);
  if (!config.themes?.[themeIndex]) return null;
  const message = (config.faceElements ?? [])
    .flatMap((face) => face.elementConfig ?? [])
    .find((el) => el.type === 'TEXT' && el.editable !== false && el.lineItemPropertyKey);
  if (!message) return null;

  return {
    optionIndex,
    route: option.clicksTo,
    productKey: meta.productKey,
    config,
    themeIndex,
    variant: config.themes[themeIndex],
    messageKey: message.lineItemPropertyKey,
    maxLength: message.maxLength,
    maxLines: message.maxLines
  };
}

/**
 * The cart line builder FINISHED hands to the cart writer for the inline card: the gold-foil picker's own
 * builder (buildUvInsertCartLineItem) on the same config, design and text the picker would hold.
 * @param {{ card: NonNullable<ReturnType<typeof findInlineCard>>, templateIds: Record<number, string>, message: string }} args
 */
export function inlineCardLineBuilder({ card, templateIds, message }) {
  return ({ giftWrapKey, parentTitle, vendor }) =>
    buildUvInsertCartLineItem({
      config: card.config,
      themeIndex: card.themeIndex,
      templateIds,
      textByKey: seedTextByKey(card.config, { [card.messageKey]: message }),
      imageByKey: {},
      giftWrapKey,
      parentTitle,
      vendor: vendor ?? null
    });
}
